import { useState } from "react";

import { refusalText } from "../../../../api/errors";
import { useApiCommand } from "../../../../api/hooks";
import type { ChangeConnectionPoint, SymbolSetItem } from "../../../../api/types";
import { Button, ShapeDrawing } from "../../../../ui";
import { connectionPoints, fmt, type ConnectionPoint } from "../../../../ui/shapeSvg";
import { useCommandId, useRefresh } from "../../hooks";
import { FormError, Select, TextInput } from "../fields";
import { ShapeMarks, ShapeRegistration, ShapeWithdrawal, withdrawnWords } from "./ShapeAbout";
import { SIDES, kindWord, originWords, usedWords } from "./words";

type Change = Omit<ChangeConnectionPoint, "name" | "command_id" | "code" | "expected_shape_version">;
type Sent = Omit<ChangeConnectionPoint, "name">;

/** What changes after a point changed: the set, and the pages that show the shapes and the lines that follow them. */
export const AFTER_SHAPE_CHANGE = ["symbol_set", "item_page", "item_snapshot", "element", "sme_card"] as const;

/** A number typed in a box: finite, with a point as the decimal mark; null when the box holds anything else. */
export function typedNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

interface Props {
  item: SymbolSetItem;
  editable: boolean;
  /** Every shape of the set, for the successor of a withdrawal. */
  items: readonly SymbolSetItem[];
  /** The name of a kind of drawing for its code (PidSheet gives "P&ID sheet"). */
  drawingName: (code: string) => string;
}

/**
 * One shape of the library, large, with its connection points. An engineer places, names, turns, confirms and removes
 * the points here (a point is dragged on the drawing and sent when it is let go; a click only chooses it). A change to
 * a library shape is kept as this discipline's change and waits for the SME with an element that shows it. Below:
 * the marks, the registration and the withdrawal of the shape.
 */
export function ShapeEditor({ item, editable, items, drawingName }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const change = useApiCommand("change_connection_point");
  const ids = useCommandId();
  const refresh = useRefresh();
  const points = connectionPoints(item.shape);
  const isSymbol = item.kind === "symbol";
  const canEdit = editable && isSymbol && !item.withdrawn;

  const send = (body: Change) => {
    if (change.isPending) return; // one change at a time: the next one is weighed against the version it saw
    change.mutate(
      { ...body, code: item.code, expected_shape_version: item.shape_version, command_id: ids.current() } satisfies Sent,
      {
        onSuccess: () => {
          ids.renew();
          refresh(...AFTER_SHAPE_CHANGE);
        },
        onError: () => ids.renew(),
      },
    );
  };

  return (
    <section className="shape-editor" aria-labelledby="shape-title">
      <div className="shape-editor__drawing">
        <ShapeDrawing
          shape={item.shape}
          kind={item.kind}
          label={`${item.kind === "line_style" ? "Line style" : "Symbol"} ${item.code}, large`}
          size={280}
          trueWidths
          showPoints
          selected={selected}
          onSelectPoint={canEdit ? setSelected : undefined}
          onMovePoint={canEdit ? (point, x, y) => send({ action: "move", point, x_mm: x, y_mm: y }) : undefined}
        />
      </div>
      <div className="stack">
        <h3 id="shape-title" tabIndex={-1}>
          <code>{item.code}</code> {item.title}
        </h3>
        <p className="muted">
          {originWords(item)}. A {kindWord(item.kind)} for {drawingName(item.kind_of_drawing)}
          {item.category ? `, ${item.category.replace(/_/g, " ")}` : ""}
          {item.discipline ? `, ${item.discipline}` : ""}. Version {item.shape_version}. {usedWords(item)}.
        </p>
        {item.withdrawn && <p className="shape-editor__withdrawn">{withdrawnWords(item)}</p>}
        {isSymbol && (
          <>
            <h4>Connection points</h4>
            {points.length === 0 ? (
              <p className="muted">No connection point yet: a line cannot attach to this symbol until one is placed.</p>
            ) : (
              <ul className="point-list" aria-label={`The connection points of ${item.code}`}>
                {points.map((p, index) => (
                  // a stable key, so that a keyboard user keeps the box or button they were in after an edit
                  <PointRow key={index} point={p} editable={canEdit} selected={selected === p.name} onSelect={() => setSelected(p.name)} send={send} busy={change.isPending} />
                ))}
              </ul>
            )}
            {canEdit && points.length < 2 && (
              <p>
                <Button variant="quiet" busy={change.isPending} onClick={() => send({ action: "guess_flow_axis", point: "" })}>
                  Offer the two ends of the flow axis
                </Button>{" "}
                <span className="muted">For a valve or another item a pipe runs through: two guessed points to confirm.</span>
              </p>
            )}
            {canEdit && <AddPoint send={send} busy={change.isPending} taken={points.map((p) => p.name)} />}
          </>
        )}
        {!isSymbol && <p className="muted">A line style has no connection points: it is drawn along a line.</p>}
        <FormError message={change.error ? refusalText(change.error) : null} />
        <ShapeMarks key={`marks:${item.code}`} item={item} editable={editable} />
        <ShapeRegistration key={`registration:${item.code}`} item={item} editable={editable} />
        <ShapeWithdrawal key={`withdrawal:${item.code}`} item={item} items={items} editable={editable} />
      </div>
    </section>
  );
}

function sideWords(direction: string): string {
  return SIDES.find((s) => s.value === direction)?.label.toLowerCase() ?? direction;
}

function PointRow({ point, editable, selected, onSelect, send, busy }: { point: ConnectionPoint; editable: boolean; selected: boolean; onSelect: () => void; send: (c: Change) => void; busy: boolean }) {
  const [name, setName] = useState(point.name);
  const [angle, setAngle] = useState(point.angle_deg === undefined ? "" : String(point.angle_deg));
  const [x, setX] = useState(fmt(point.x_mm));
  const [y, setY] = useState(fmt(point.y_mm));
  const [problem, setProblem] = useState<string | null>(null);
  // The boxes show what the server holds now, after this or another person's change: they are set again when the point
  // changes (during the render, the way React keeps state in step with its props), without making the row again.
  const stamp = `${point.name}|${point.x_mm}|${point.y_mm}|${point.angle_deg ?? ""}`;
  const [shown, setShown] = useState(stamp);
  if (shown !== stamp) {
    setShown(stamp);
    setName(point.name);
    setAngle(point.angle_deg === undefined ? "" : String(point.angle_deg));
    setX(fmt(point.x_mm));
    setY(fmt(point.y_mm));
    setProblem(null);
  }
  const words = `${point.name}: ${sideWords(point.direction)}${point.angle_deg !== undefined ? `, ${fmt(point.angle_deg)} degrees` : ""}, at ${fmt(point.x_mm)}, ${fmt(point.y_mm)} mm`;

  const move = () => {
    const nx = typedNumber(x);
    const ny = typedNumber(y);
    if (nx === null || ny === null) {
      setProblem(`${nx === null ? "x" : "y"} must be a number, with a point as the decimal mark (for example 1.5). Nothing was sent.`);
      return;
    }
    setProblem(null);
    send({ action: "move", point: point.name, x_mm: nx, y_mm: ny });
  };
  const setExactAngle = () => {
    const value = typedNumber(angle);
    if (value === null) {
      setProblem("The angle must be a number of degrees, with a point as the decimal mark (for example 45). Nothing was sent.");
      return;
    }
    setProblem(null);
    send({ action: "set_side", point: point.name, angle_deg: value });
  };

  return (
    <li className="point" data-selected={selected || undefined}>
      <p>
        <Button variant="quiet" onClick={onSelect} aria-pressed={selected}>
          {words}
        </Button>
        {point.unsure && <span className="muted"> (guessed from the drawing, not confirmed yet)</span>}
        {point.role_hint && <span className="muted"> Role hint: {point.role_hint}.</span>}
      </p>
      {editable && (
        // The buttons are not disabled while a change is sent (a disabled button loses the keyboard's focus); a second
        // press while one is on its way is not sent.
        <div className="point__steps" aria-busy={busy || undefined}>
          {point.unsure && (
            <Button variant="primary" onClick={() => send({ action: "confirm", point: point.name })}>
              Confirm {point.name}
            </Button>
          )}
          <Select label={`Side of ${point.name}`} value={point.direction} options={SIDES} onChange={(direction) => send({ action: "set_side", point: point.name, direction: direction as ChangeConnectionPoint["direction"] })} />
          <TextInput label={`Exact angle of ${point.name}`} hint="Degrees: 0 right, 90 down, 180 left, 270 up." value={angle} onChange={setAngle} />
          <Button variant="quiet" onClick={setExactAngle}>
            Set the angle of {point.name}
          </Button>
          <TextInput label={`x of ${point.name} (mm)`} value={x} onChange={setX} />
          <TextInput label={`y of ${point.name} (mm)`} value={y} onChange={setY} />
          <Button variant="quiet" onClick={move}>
            Move {point.name}
          </Button>
          <TextInput label={`New name of ${point.name}`} value={name} onChange={setName} />
          <Button variant="quiet" onClick={() => name.trim() && name.trim() !== point.name && send({ action: "rename", point: point.name, new_name: name.trim() })}>
            Rename {point.name}
          </Button>
          <Button variant="quiet" onClick={() => send({ action: "remove", point: point.name })}>
            Remove {point.name}
          </Button>
          <FormError message={problem} />
        </div>
      )}
    </li>
  );
}

function AddPoint({ send, busy, taken }: { send: (c: Change) => void; busy: boolean; taken: string[] }) {
  const [name, setName] = useState("");
  const [side, setSide] = useState("east");
  const [problem, setProblem] = useState<string | null>(null);
  const add = () => {
    const wanted = name.trim();
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(wanted)) {
      setProblem("A point name starts with a letter and uses letters, digits and underscores, for example P1 or suction.");
      return;
    }
    if (taken.includes(wanted)) {
      setProblem(`This symbol has a point named ${wanted} already.`);
      return;
    }
    setProblem(null);
    send({ action: "add", point: wanted, direction: side as ChangeConnectionPoint["direction"], x_mm: 0, y_mm: 0 });
    setName("");
  };
  return (
    <div className="point-add" role="group" aria-label="Add a connection point">
      <TextInput label="Name of a new point" hint="It starts at the reference point; drag it into place." value={name} onChange={setName} />
      <Select label="Its side" value={side} options={SIDES} onChange={setSide} />
      <Button variant="quiet" busy={busy} onClick={add}>
        Add the point
      </Button>
      <FormError message={problem} />
    </div>
  );
}
